import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  Defaults,
  type InfiniteLoaderListProps as InfiniteLoaderListPropsType,
} from '@pbt/pbt-ui-components'

import type { Estimate as GraphqlEstimate } from '~/api/graphql/generated/types'
import type { PuiDataTableColumn } from '~/components/common/lists/pui-data-table/puiDataTableType'
import {
  fetchMoreItemsForClientPaymentActivity,
  fetchPaymentPage,
  getClientBillingActivityPaymentError,
  getPaymentLoading,
  getPaymentPageData,
  getPaymentTotalCount,
  getShouldRefreshData,
  getUnappliedPaymentLoading,
  getUnappliedPaymentPageData,
  getUnappliedPaymentTotalCount,
} from '~/store/duck/clientBillingActivityData'

import {
  getExpandedWidgetLimit,
  getExpandedWidgetOffset,
} from '../table/common/expandedWidgetUtils'
import { useGetPaymentBillingActivityColumns } from '../table/payments/columns'
import {
  ClientBillingActivityInfiniteLoaderList,
  type ClientBillingActivityInfiniteLoaderListClassesType,
} from './ClientBillingActivityInfiniteLoaderList'
import { useFetchOnInitialSyncQueryParams } from './hooks'

interface PaymentsInfiniteLoaderListProps {
  InfiniteLoaderListProps?: Partial<InfiniteLoaderListPropsType>
  classes?: ClientBillingActivityInfiniteLoaderListClassesType
  clientId: string | undefined
  hasError?: boolean
  initialItem?: number
  onCheckPayments?: (id: string) => void
  onClickEstimate: (data: GraphqlEstimate) => void
  paymentLabelAndAmount?: boolean
  selectedPaymentIds?: string[]
  sortColumns?: (columns: PuiDataTableColumn[]) => PuiDataTableColumn[]
  supportBiDirectionalInfiniteScroll?: boolean
  unapplied: boolean
}

export const PaymentsInfiniteLoaderList = ({
  InfiniteLoaderListProps,
  classes,
  clientId,
  hasError = false,
  onCheckPayments,
  onClickEstimate,
  selectedPaymentIds,
  sortColumns,
  unapplied,
  paymentLabelAndAmount,
  initialItem: initialItemParam = 0,
  supportBiDirectionalInfiniteScroll = false,
}: PaymentsInfiniteLoaderListProps) => {
  const dispatch = useDispatch()
  const { t } = useTranslation('Common')

  const refresh = useSelector(getShouldRefreshData)
  const paymentPageData = useSelector(getPaymentPageData)
  const paymentTotalCount = useSelector(getPaymentTotalCount)
  const isLoading = useSelector(getPaymentLoading)
  const unappliedPaymentPageData = useSelector(getUnappliedPaymentPageData)
  const unappliedPaymentTotalCount = useSelector(getUnappliedPaymentTotalCount)
  const unappliedIsLoading = useSelector(getUnappliedPaymentLoading)
  const error = useSelector(getClientBillingActivityPaymentError)

  const [offsetValue, setOffsetValue] = useState(0)

  const handleFetchMore = ({ from, to }: { from: number; to: number }) => {
    if (clientId) {
      const offset = from < 10 && supportBiDirectionalInfiniteScroll ? 0 : from
      const limit =
        to && supportBiDirectionalInfiniteScroll
          ? to - offset + 1
          : Defaults.INFINITE_LIST_BATCH_LOAD_COUNT
      setOffsetValue(offset)
      dispatch(
        fetchMoreItemsForClientPaymentActivity({
          unapplied,
          clientId,
          from: offset,
          to: limit,
        }),
      )
    }
  }

  useEffect(() => {
    if (clientId && refresh) {
      const limit = initialItemParam
        ? Defaults.INFINITE_LIST_BATCH_LOAD_COUNT * 2
        : Defaults.INFINITE_LIST_BATCH_LOAD_COUNT
      dispatch(
        fetchPaymentPage({
          unapplied,
          clientId,
          offset: offsetValue,
          limit,
          isRefreshing: true,
        }),
      )
    }
  }, [clientId, refresh, unapplied])

  // Fetch initial data with proper payment params when necessary
  useFetchOnInitialSyncQueryParams({
    key: 'Payments',
    onFetch: () => {
      if (!clientId) {
        return
      }
      const offset = getExpandedWidgetOffset(
        initialItemParam,
        supportBiDirectionalInfiniteScroll,
      )
      const limit = getExpandedWidgetLimit(
        initialItemParam,
        supportBiDirectionalInfiniteScroll,
      )
      setOffsetValue(offset)
      dispatch(
        fetchPaymentPage({
          unapplied,
          clientId,
          offset,
          limit,
          fillPrecedingWithNull:
            offset > 0 && supportBiDirectionalInfiniteScroll,
        }),
      )
    },
  })

  const columns = useGetPaymentBillingActivityColumns({
    key: 'value',
    onClickEstimate,
    supportBiDirectionalInfiniteScroll,
    checkbox: onCheckPayments
      ? {
          hasError,
          onCheck: onCheckPayments,
          getIsChecked: (id) => (selectedPaymentIds || []).includes(id),
        }
      : undefined,
    showUnappliedAmount: unapplied,
    showPaymentLabelAndAmount: paymentLabelAndAmount,
  }) as PuiDataTableColumn[]
  const sortedColumns = sortColumns ? sortColumns(columns) : columns

  return (
    <ClientBillingActivityInfiniteLoaderList
      InfiniteLoaderListProps={InfiniteLoaderListProps}
      classes={classes}
      clientId={clientId}
      columns={sortedColumns}
      emptyPlaceholder={t('Common:NO_PAYMENTS')}
      error={error}
      initialItemParam={initialItemParam}
      isLoadingInitialData={unapplied ? unappliedIsLoading : isLoading}
      pageData={unapplied ? unappliedPaymentPageData : paymentPageData}
      totalCount={unapplied ? unappliedPaymentTotalCount : paymentTotalCount}
      onFetchMore={handleFetchMore}
    />
  )
}
